/**
 * SubjectFilter — single-select pill dropdown used on Advanced Results
 * and Quick Check results to narrow the shortlist to one of the subjects
 * the family picked in the wizard.
 *
 * Shown ONLY when the applicant picked more than one intended subject.
 * The empty value ("") means "All subjects" — restores the full pool.
 * The option labels are the ORIGINAL chip strings (casing preserved)
 * the family typed on the wizard, so the dropdown feels like a natural
 * echo of their own choices.
 *
 * Filtering itself is client-side against the per-row `matched_intended`
 * list attached by the backend engines — instant, and preserves the
 * existing match ranking.
 */
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

export default function SubjectFilter({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Hide entirely when the applicant only picked one subject (or none).
  // Filtering a single-subject shortlist to that same subject is a
  // no-op, so the chip would just be visual noise.
  if (!Array.isArray(options) || options.length <= 1) return null;

  const active = !!value;
  const label = active ? value : "Subject";

  return (
    <div ref={ref} className="relative inline-block" data-testid="subject-filter">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="subject-filter-toggle"
        aria-expanded={open}
        aria-haspopup="listbox"
        className={
          "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-underground-sc tracking-wider transition-colors " +
          (active
            ? "border-[#4C1D95] bg-[#4C1D95] text-white hover:bg-[#3a1476]"
            : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]")
        }
      >
        {label}
        <ChevronDown className="h-3 w-3 opacity-80" />
      </button>

      {open && (
        <div
          data-testid="subject-filter-menu"
          className="absolute z-30 mt-2 min-w-[220px] rounded-md border border-stone-200 bg-white shadow-lg py-1 text-sm"
          role="listbox"
        >
          <button
            type="button"
            role="option"
            aria-selected={!active}
            onClick={() => { onChange(""); setOpen(false); }}
            data-testid="subject-filter-option-all"
            className={
              "w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-stone-50 " +
              (!active ? "text-[#4C1D95] font-medium" : "text-stone-700")
            }
          >
            <span
              className={
                "h-4 w-4 rounded-full border flex items-center justify-center shrink-0 " +
                (!active
                  ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                  : "border-stone-300 bg-white text-transparent")
              }
              aria-hidden
            >
              <Check className="h-2.5 w-2.5" strokeWidth={3} />
            </span>
            All subjects
          </button>
          <div className="my-1 border-t border-stone-100" />
          {options.map((subj) => {
            const on = value === subj;
            return (
              <button
                key={subj}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => { onChange(subj); setOpen(false); }}
                data-testid={`subject-filter-option-${subj.toLowerCase().replace(/\s+/g, "-")}`}
                className={
                  "w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-stone-50 " +
                  (on ? "text-[#4C1D95] font-medium" : "text-stone-700")
                }
              >
                <span
                  className={
                    "h-4 w-4 rounded-full border flex items-center justify-center shrink-0 " +
                    (on
                      ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                      : "border-stone-300 bg-white text-transparent")
                  }
                  aria-hidden
                >
                  <Check className="h-2.5 w-2.5" strokeWidth={3} />
                </span>
                {subj}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
